import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';

import { useAppContext, ACTION_TYPES } from '../../../Context/AppContext';
import { makeApiRequest } from '../../../Shared/Api';
import EditCustomer from '../../../Components/Modals/EditCustomer';
import { JobDetailContext } from '../context/JobDetailContext';
import useJobDetail from '../hooks/useJobDetail';
import BackButton from './components/BackButton';
import JobHero from './components/JobHero';
import JobErrorState from './components/JobErrorState';
import JobMainContent from './components/JobMainContent';
import JobSidebar from './components/JobSidebar';
import JobActivitySection from './components/JobActivitySection';
import JobSkeleton from './components/JobSkeleton';

const JobDetailPage = () => {
	const navigate = useNavigate();
	const { id: urlJobId } = useParams();
	const { dispatch: dispatchApp } = useAppContext();
	const { state: jobDetailState } = useContext(JobDetailContext);
	const { enqueueSnackbar } = useSnackbar();
	const jobId = jobDetailState.selectedJobId || urlJobId;

	const {
		job,
		loading,
		error,
		fetchJob,
		patchJobField,
		patchJobFields,
		updateJobLocal,
		clearJob,
	} = useJobDetail(jobId);

	const [isEditingNotes, setIsEditingNotes] = useState(false);
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [customerEditLoading, setCustomerEditLoading] = useState(false);
	const [emailEventsRefreshKey, setEmailEventsRefreshKey] = useState(0);

	const refreshJob = useCallback(async () => {
		await fetchJob();
		setEmailEventsRefreshKey((key) => key + 1);
	}, [fetchJob]);

	useEffect(() => {
		if (job) {
			dispatchApp({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: {
					pageTitle: `${job.customer_name} / ${job.name}`,
				},
			});
		}
		return () => {
			dispatchApp({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [job, dispatchApp]);

	const handleChange = (value, key) => {
		updateJobLocal({ [key]: value });
	};

	const handleCustomerEdit = async () => {
		if (!job?.customer) return;

		setCustomerEditLoading(true);
		try {
			const customer = await makeApiRequest(
				'GET',
				`/customers/${job.customer}/`,
			);
			setSelectedCustomer(customer);
			setIsCustomerModalOpen(true);
		} catch (err) {
			enqueueSnackbar(
				err.message || 'Failed to load customer. Please try again.',
				{ variant: 'error' },
			);
		} finally {
			setCustomerEditLoading(false);
		}
	};

	const handleCustomerUpdated = (updatedCustomer) => {
		updateJobLocal({ customer_name: updatedCustomer.name });
	};

	const handleBack = () => {
		clearJob();
		navigate('/jobs');
	};

	const handleFieldToggle = (field) => (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (field === 'notes') {
			if (isEditingNotes) {
				patchJobField(job.notes, 'notes');
			}
			setIsEditingNotes((prev) => !prev);
		} else if (field === 'description') {
			if (isEditingDescription) {
				patchJobField(job.description, 'description');
			}
			setIsEditingDescription((prev) => !prev);
		}
	};

	if (loading) {
		return <JobSkeleton />;
	}

	if (error) {
		return <JobErrorState error={error} onBack={handleBack} />;
	}

	if (!job) return null;

	return (
		<Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
			<BackButton onClick={handleBack} />

			<JobHero
				customerName={job.customer_name}
				jobName={job.name}
				description={job.description}
				isEditingDescription={isEditingDescription}
				onDescriptionToggle={handleFieldToggle('description')}
				onDescriptionChange={(value) => handleChange(value, 'description')}
				onDescriptionConfirm={(value) => {
					patchJobField(value, 'description');
					setIsEditingDescription(false);
				}}
				dueDate={job.due_date}
				onDueDateChange={(value) => patchJobField(value, 'due_date')}
				job={job}
				onSiteSave={(fields) => patchJobFields(fields)}
				bidAmount={job.bid_details?.amount}
				bidStatus={job.bid_details?.status ?? job.bid_status}
				bidDetails={job.bid_details}
				depositPaidAmount={job.deposit_paid_amount}
				refundedAmount={job.refunded_amount}
				refundSummary={job.refund_summary}
				showDepositSummary
				onCustomerEdit={handleCustomerEdit}
				customerEditLoading={customerEditLoading}
			/>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' },
					gap: { xs: 2.5, md: 3 },
					alignItems: 'start',
				}}
			>
				<Box sx={{ order: { xs: 2, lg: 1 }, minWidth: 0 }}>
					<Stack spacing={{ xs: 2.5, md: 3 }}>
						<JobMainContent
							jobId={jobId}
							selectedJob={job}
							isEditingNotes={isEditingNotes}
							onNotesToggle={handleFieldToggle('notes')}
							onChange={handleChange}
							onNotesConfirm={(v) => {
								patchJobField(v, 'notes');
								setIsEditingNotes(false);
							}}
							onJobUpdated={refreshJob}
						/>
						<JobActivitySection
							jobId={jobId}
							bids={job.bids}
							paymentTimeline={job.payment_timeline}
							emailEventsRefreshKey={emailEventsRefreshKey}
						/>
					</Stack>
				</Box>

				<Box sx={{ order: { xs: 1, lg: 2 } }}>
					<JobSidebar
						jobId={jobId}
						bidDetails={job.bid_details}
						notes={job.notes}
						isEditingNotes={isEditingNotes}
						onNotesToggle={handleFieldToggle('notes')}
						onNotesChange={(value) => handleChange(value, 'notes')}
						onNotesConfirm={(v) => {
							patchJobField(v, 'notes');
							setIsEditingNotes(false);
						}}
						onJobUpdated={refreshJob}
					/>
				</Box>
			</Box>
			<EditCustomer
				open={isCustomerModalOpen}
				onClose={() => {
					setIsCustomerModalOpen(false);
					setSelectedCustomer(null);
				}}
				onSubmit={handleCustomerUpdated}
				selectedCustomer={selectedCustomer}
			/>
		</Box>
	);
};

export default JobDetailPage;
